import React from 'react'
import Chart from 'react-apexcharts'
import clsx from 'clsx'
import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils'

type Props = {
  id: string
  title: React.ReactNode
  displayRow?: boolean
  showTotal?: boolean
  isMoney?: boolean
  data: {
    label: string
    value: number
    colorClass?: string
  }[]
  height: number
  isLoading: boolean
}

export function DonutCard({
  id,
  title,
  data,
  height,
  isLoading,
  displayRow,
  showTotal,
  isMoney
}: Props) {
  const fallback = ['primary', 'success', 'info', 'danger', 'warning', 'dark']
  const colorClasses = data.map((item, i) => item.colorClass || fallback[i % fallback.length])

  const series = data.map((item) => item.value)
  const max = series.reduce((acc, val) => acc + val, 0)
  const labels = data.map((item) => item.label)
  const colors = colorClasses.map((c) => getCSSVariableValue(`--bs-text-${c}`))

  const formatValue = (value: number, isMoney?: boolean) => {
    if (!isMoney) return value.toString()
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const options = {
    chart: {
      id,
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: true },
    },
    labels,
    colors,
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (val: number) => formatValue(val, isMoney)
      }
    }
  }

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title">
          <span className="card-label fw-bold fs-3 mb-1">
            {title}
            {showTotal && (
              <>
                {isLoading ? (
                  <i className="spinner-border fs-5x ms-2" />
                ) : (
                  <span className="ms-2 text-danger fw-bold">
                    {formatValue((data || []).reduce((acc, item) => acc + item.value, 0), isMoney)}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      </div>

      <div className="card-body p-9">
        {isLoading ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <i className="spinner-border fs-5x" />
          </div>
        ) : data.length === 0 ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <span>No data</span>
          </div>
        ) : (
          <div className="row align-items-center mx-0 w-100">
            <div className={clsx(
              'col-12 d-flex justify-content-center px-0 mb-4 mb-md-0',
              displayRow !== true ? 'col-md-6' : ''
            )}>
              <Chart options={options} series={series} type="donut" height={height} />
            </div>
            <div className={clsx(
              'col-12 px-0',
              displayRow !== true ? 'col-md-6' : 'mt-8'
            )}>
              <div className="d-flex flex-column justify-content-center">
                {data.map((item, i) => (
                  <div
                    key={i}
                    className="d-flex fs-6 fw-semibold align-items-center mb-2"
                    title={formatValue(item.value, isMoney)}
                  >
                    <div className={`bullet bg-${colorClasses[i]} me-3`} />
                    <div className="fs-5 fw-bold text-gray-600 me-5">{item.label}</div>
                    <div className="ms-auto fw-bolder text-gray-700 text-end">
                      {formatValue(item.value, isMoney)} ({max > 0 ? ((item.value * 100) / max).toFixed(2) : '0.00'}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
